/* eslint-disable */
/**
 * OwnerTour — First-login tour for newly-assigned Owners / Managers.
 *
 * Session 60.2 (Batch D) · 6-step overlay walkthrough that COMPLEMENTS the
 * existing 15-item onboarding checklist. It never blocks the dashboard,
 * never forces experienced owners through it, and is fully:
 *   • Dismissible — Skip / X closes the tour and records `tour_completed`
 *     ack via /api/owner/onboarding-ack.
 *   • Resumable — current step is tracked in localStorage per venue so a
 *     refresh mid-tour picks up where you left off.
 *   • Idempotent — reads `/api/owner/onboarding-status?venue_id=...` and
 *     will not auto-open once `tour_completed` is set for that venue.
 *
 * Copy: verbatim to Cam's Feb 2026 spec. NO em dashes.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";

const STEPS = [
  {
    key: "welcome",
    title: "Welcome to your workspace dashboard",
    body: (
      <>
        <p>
          This is where you manage everything about your workspace presence
          on Métier. Take a minute to look around, and come back to this tour
          any time from the checklist.
        </p>
        <p style={{ marginTop: 12, fontSize: 13, opacity: 0.75 }}>
          You can dismiss this tour at any point and pick it up later from
          the setup checklist.
        </p>
      </>
    ),
    cta: "Start the tour",
  },
  {
    key: "listing",
    title: "Review your listing",
    body: (
      <p>
        Confirm the venue name, description, address, workspace types,
        amenities, and photos. Everything a potential guest sees starts here.
      </p>
    ),
    cta: "Next: pricing and booking",
  },
  {
    key: "pricing",
    title: "Set pricing and booking",
    body: (
      <p>
        Choose your rates, decide whether to accept instant bookings or
        request-based reservations, and set availability, buffers, and
        cancellation policies.
      </p>
    ),
    cta: "Next: payments",
  },
  {
    key: "payments",
    title: "Payments",
    body: (
      <>
        <p>
          Métier routes payouts through Stripe Connect. You can review your
          Stripe status here.
        </p>
        <p style={{ marginTop: 10, fontSize: 13, opacity: 0.75 }}>
          Only Owners can modify Stripe settings. Managers and Staff can view
          the connection status.
        </p>
      </>
    ),
    cta: "Next: reservations",
  },
  {
    key: "reservations",
    title: "Reservations",
    body: (
      <p>
        Requests, confirmed reservations, cancellations, and refunds all live
        under Reservations. What you can do here depends on your role.
      </p>
    ),
    cta: "Next: go live",
  },
  {
    key: "golive",
    title: "Go live",
    body: (
      <p>
        Preview your public listing, finish any remaining setup items on the
        checklist, and publish when you are ready.
      </p>
    ),
    cta: "Finish tour",
  },
];

function tourKey(venueId) {
  return `metier.ownerTour.step.${venueId || "default"}`;
}

export default function OwnerTour({ venueId, forceOpen = false, onClose }) {
  const [open, setOpen] = useState(false);
  const [idx, setIdx] = useState(0);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState(null); // onboarding-status payload

  // Fetch onboarding status; open if `tour_completed` ack is NOT present.
  useEffect(() => {
    if (!venueId) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get(`/owner/onboarding-status?venue_id=${encodeURIComponent(venueId)}`);
        if (cancelled) return;
        setStatus(r.data);
        const tourStep = (r.data?.steps || []).find((s) => s.key === "tour_completed");
        const alreadyDone = tourStep?.done;
        if (forceOpen || !alreadyDone) {
          const stored = Number(localStorage.getItem(tourKey(venueId)) || 0);
          setIdx(Math.min(Math.max(stored, 0), STEPS.length - 1));
          setOpen(true);
        }
      } catch { /* silent — tour is best effort */ }
    })();
    return () => { cancelled = true; };
  }, [venueId, forceOpen]);

  const persistStep = useCallback((n) => {
    try { localStorage.setItem(tourKey(venueId), String(n)); } catch { /* ignore */ }
  }, [venueId]);

  const finish = useCallback(async (markComplete) => {
    setBusy(true);
    try {
      if (markComplete) {
        await api.post("/owner/onboarding-ack", {
          venue_id: venueId,
          step_key: "tour_completed",
          acknowledged: true,
        });
      }
      try { localStorage.removeItem(tourKey(venueId)); } catch { /* ignore */ }
    } catch { /* silent */ }
    setOpen(false);
    setBusy(false);
    onClose?.();
  }, [venueId, onClose]);

  const next = () => {
    if (idx >= STEPS.length - 1) return finish(true);
    const n = idx + 1;
    setIdx(n);
    persistStep(n);
  };
  const back = () => {
    if (idx === 0) return;
    const n = idx - 1;
    setIdx(n);
    persistStep(n);
  };

  const step = useMemo(() => STEPS[idx], [idx]);
  if (!open || !step) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="owner-tour-title"
      data-testid="owner-tour-modal"
      style={{
        position: "fixed", inset: 0, zIndex: 9999,
        background: "rgba(11,17,34,0.55)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: 20, backdropFilter: "blur(3px)",
      }}
    >
      <div
        data-testid={`owner-tour-step-${step.key}`}
        style={{
          background: "#FAF8F3",
          maxWidth: 520, width: "100%",
          borderRadius: 14, padding: "28px 28px 22px",
          boxShadow: "0 24px 60px rgba(11,17,34,0.35)",
          fontFamily: "system-ui, -apple-system, 'Segoe UI', sans-serif",
          color: "#1B2340",
          position: "relative",
        }}
      >
        <button
          type="button"
          onClick={() => finish(true)}
          disabled={busy}
          data-testid="owner-tour-skip"
          aria-label="Skip tour"
          style={{
            position: "absolute", top: 14, right: 14,
            background: "none", border: "none",
            color: "rgba(27,35,64,0.55)", cursor: "pointer",
            fontSize: 20, lineHeight: 1, padding: 4,
          }}
        >&times;</button>

        <div style={{
          fontSize: 11, letterSpacing: "0.16em", textTransform: "uppercase",
          fontWeight: 600, color: "#B8913A", marginBottom: 8,
        }}>
          Step {idx + 1} of {STEPS.length}
        </div>
        <h2
          id="owner-tour-title"
          style={{ fontFamily: "'Playfair Display', Georgia, serif",
                   fontSize: 26, margin: "0 0 12px", lineHeight: 1.2 }}
        >
          {step.title}
        </h2>
        <div style={{ fontSize: 15, lineHeight: 1.55, color: "rgba(27,35,64,0.85)" }}>
          {step.body}
        </div>

        {/* Progress bar */}
        <div style={{
          display: "flex", gap: 6, marginTop: 20, marginBottom: 20,
        }}>
          {STEPS.map((s, i) => (
            <div key={s.key} style={{
              height: 4, flex: 1, borderRadius: 2,
              background: i <= idx ? "#B8913A" : "rgba(27,35,64,0.12)",
              transition: "background 200ms",
            }} />
          ))}
        </div>

        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
          <button
            type="button"
            onClick={idx === 0 ? () => finish(false) : back}
            disabled={busy}
            data-testid="owner-tour-back"
            style={{
              background: "none", border: "none", color: "rgba(27,35,64,0.65)",
              cursor: "pointer", fontSize: 14, padding: "8px 4px",
            }}
          >
            {idx === 0 ? "Skip for now" : "← Back"}
          </button>
          <button
            type="button"
            onClick={next}
            disabled={busy}
            data-testid="owner-tour-next"
            style={{
              background: "#1B2340", color: "#FAF8F3", border: "none",
              padding: "10px 20px", borderRadius: 999, fontWeight: 600,
              fontSize: 14, cursor: "pointer",
              opacity: busy ? 0.7 : 1,
            }}
          >
            {step.cta}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Tour trigger link — for the setup checklist so an owner can re-open the
 * tour any time. Renders as an inline gold link.
 */
export function ReopenTourLink({ onOpen }) {
  return (
    <button
      type="button"
      onClick={onOpen}
      data-testid="owner-tour-reopen"
      style={{
        background: "none", border: "none", color: "#B8913A",
        textDecoration: "underline", cursor: "pointer", fontSize: 13,
        padding: 0,
      }}
    >
      Restart the first-login tour
    </button>
  );
}
